import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { useUserTenantPreference } from './preferences';

export interface Bookmark {
  title: string;
  path: string;
}

export interface BookmarkUserPreference {
  bookmarks: Bookmark[] | undefined;
}

export interface useBookmarksOptions {
  onSave?: () => void;
}

export const useBookmarks = (options: useBookmarksOptions = {}) => {
  const { onSave } = options;

  const {
    location: { pathname: currentPathName },
  } = useHistory();

  const {
    value: preference,
    setPreference: setBookmarks,
    loading,
  } = useUserTenantPreference<BookmarkUserPreference>({ key: PREFERENCE_KEYS.BOOKMARKS });

  const bookmarks = useMemo(() => preference?.bookmarks ?? [], [preference]);

  const currentBookmark = useMemo(
    () => bookmarks.find((bookmark) => bookmark.path === currentPathName),
    [bookmarks, currentPathName]
  );

  const isBookmarked = useMemo(() => !!currentBookmark, [currentBookmark]);

  const addBookmark = useCallback(
    (newBookmark: Bookmark) => {
      setBookmarks({ bookmarks: [...bookmarks, newBookmark] });
    },
    [bookmarks, setBookmarks]
  );
  const removeBookmark = useCallback(
    (target: string) => {
      if (bookmarks.filter((bookmark) => bookmark.path === target).length) {
        setBookmarks({ bookmarks: bookmarks.filter((bookmark) => bookmark.path !== target) });
      }
    },
    [bookmarks, setBookmarks]
  );

  const unbookmarkPage = useCallback(() => {
    removeBookmark(currentPathName);
  }, [currentPathName, removeBookmark]);

  const bookmarkPage = useCallback(
    (title: string) => {
      if (title.trim().length) {
        addBookmark({ title, path: currentPathName });
        onSave?.();
      }
    },
    [addBookmark, currentPathName, onSave]
  );

  const renameBookmark = useCallback(
    (path: string, newName: string) => {
      const index = bookmarks.findIndex((bookmark) => bookmark.path === path);
      const newBookmarks = [...bookmarks];
      newBookmarks.splice(index, 1, { title: newName, path });
      setBookmarks({ bookmarks: newBookmarks });
    },
    [bookmarks, setBookmarks]
  );

  return {
    bookmarks,
    currentBookmark,
    isBookmarked,
    addBookmark,
    removeBookmark,
    bookmarkPage,
    unbookmarkPage,
    renameBookmark,
    loading,
  };
};
